import { performRequest, performRequestSecond } from 'common/apiHandler';
import { default as apiEndPoints } from 'common/apiEndPoints';
import { getTenantDetails } from 'utils/utils';

export const stageUpdate = (sk, stage, is_lead = false, timeEntryData = '', excludeFromLoading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let cardSk = sk ? encodeURIComponent(sk) : '';
  let laneSk = stage ? encodeURIComponent(stage) : '';
  return performRequest(
    apiEndPoints.methodType.PATCH,
    `${apiEndPoints.stageUpdate}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&sk=${cardSk}&stage=${laneSk}${
      is_lead ? `&is_lead=${is_lead}` : ''
    }`,
    {},
    timeEntryData,
    true,
    excludeFromLoading
  );
};

export const manageColumnData = (data, is_lead = false, timeEntryData = '', excludeFromLoading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestSecond(
    apiEndPoints.methodType.PATCH,
    `${apiEndPoints.manageTask}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${is_lead ? `&is_lead=${is_lead}` : ''}`,
    data,
    timeEntryData,
    true,
    excludeFromLoading
  );
};

/**
 * List tasks using Task List v2 API
 * @param {Object} params
 * @param {Object} params.data - Request body with filters (task_stage_ids, task_stage_id, practice_area, firm_user_ids, priority, due_date_filter, page, limit, sort_type)
 * @param {string|boolean} params.keyword - Search keyword (optional)
 * @param {number} params.limit - Results limit per page/stage
 * @param {boolean} params.excludeFromLoading - Whether to exclude from global loading indicator
 * @returns {Promise} API response
 */
export const listTasksV2 = ({
  data = false,
  keyword = false,
  limit = 10,
  excludeFromLoading = false,
  case_id = undefined,
  default_task_stage = null,
}) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestSecond(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.listTasksV2}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${keyword ? `&keyword=${keyword}` : ''}&limit=${limit}${
      case_id ? `&case_id=${case_id}` : ''
    }${default_task_stage ? `&default_task_stage=${default_task_stage}` : ''}`,
    data,
    '',
    true,
    excludeFromLoading
  );
};
/**
 * List tasks using Task Agenda View API
 * @param {Object} params
 * @param {Object} params.task_stage_id - Task stage ID
 * @param {Object} params.method - Method type (GET, POST, PUT, DELETE)
 * @param {Object} params.limit - Results limit per page/stage
 * @param {Object} params.page - Page number
 * @param {Object} params.data - Request body with filters (practice_area, firm_user_ids, priority, due_date_filter, sort_type)
 * @param {boolean} params.excludeFromLoading - Whether to exclude from global loading
 * @returns {Promise} API response
 */
export const listTaskAgendaView = ({
  method = 'GET',
  task_stage_id = '',
  limit,
  page,
  data = {},
  case_id = null,
  default_task_stage = null,
  excludeFromLoading = true,
}) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestSecond(
    apiEndPoints.methodType[method],
    `${apiEndPoints.listTaskAgendaView}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${
      task_stage_id ? `&task_stage_id=${task_stage_id}` : ''
    }${limit ? `&limit=${limit}` : ''}${page ? `&page=${page}` : ''}${case_id ? `&case_id=${case_id}` : ''}${
      default_task_stage ? `&default_task_stage=${default_task_stage}` : ''
    }`,
    data,
    '',
    true,
    excludeFromLoading
  );
};

/**
 * Update task details
 * @param {string} task_sk - Task sort key
 * @param {Object} data - Updated task data
 * @param {boolean} excludeFromLoading - Whether to exclude from global loading
 * @returns {Promise} API response
 */
export const updateTaskDetails = (task_sk, data, excludeFromLoading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let taskSK = task_sk ? encodeURIComponent(task_sk) : '';
  return performRequest(
    apiEndPoints.methodType.PUT,
    `${apiEndPoints.updateTaskDetails}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&task_sk=${taskSK}`,
    data,
    '',
    true,
    excludeFromLoading
  );
};

/**
 * Delete a task
 * @param {string} task_sk - Task sort key
 * @returns {Promise} API response
 */
export const deleteTask = (task_sk) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let taskSK = task_sk ? encodeURIComponent(task_sk) : '';
  return performRequest(
    apiEndPoints.methodType.DELETE,
    `${apiEndPoints.deleteTask}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&task_sk=${taskSK}`
  );
};

/**
 * List task stages
 * @param {Object} params
 * @param {number} params.pageLimit - Number of items per page
 * @param {string} params.pagination_token - Pagination token
 * @param {string} params.pagination_direction - Direction (forward/backward)
 * @param {string} params.task_stage_status - Status filter (ACTIVE/INACTIVE)
 * @param {Object} data - Additional filter data
 * @param {boolean} excludeFromLoading - Whether to exclude from global loading
 * @returns {Promise} API response
 */
export const listTaskStages = (
  { pageLimit, pagination_token = '', pagination_direction = 'forward', task_stage_status = '' },
  data = {},
  excludeFromLoading = false
) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequest(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.getTaskStages}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&limit=${pageLimit}&pagination_token=${pagination_token}&pagination_direction=${pagination_direction}&task_stage_status=${task_stage_status}`,
    data,
    '',
    true,
    excludeFromLoading
  );
};

/**
 * Create a new task stage
 * @param {Object} data - Task stage data
 * @returns {Promise} API response
 */
export const createTaskStage = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequest(apiEndPoints.methodType.POST, `${apiEndPoints.createTaskStage}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`, data);
};

/**
 * Update a task stage
 * @param {Object} data - Updated task stage data
 * @returns {Promise} API response
 */
export const updateTaskStage = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequest(apiEndPoints.methodType.PUT, `${apiEndPoints.updateTaskStage}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`, data);
};

/**
 * Delete a task stage
 * @param {string} pk - Primary key
 * @param {string} sk - Sort key
 * @param {string} task_stage_status - Status to set (INACTIVE)
 * @param {boolean} excludeFromLoading - Whether to exclude from global loading
 * @returns {Promise} API response
 */
export const deleteTaskStage = (pk, sk, task_stage_status = '', excludeFromLoading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let skNew = sk.replace('#', '%23');
  let skModified = skNew.replace('#', '%23');
  let pkNew = pk.replace('#', '%23');

  return performRequest(
    apiEndPoints.methodType.DELETE,
    `${apiEndPoints.deleteTaskStage}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&pk=${pkNew}&sk=${skModified}&task_stage_status=${task_stage_status}`,
    null,
    '',
    true,
    excludeFromLoading
  );
};
